<template>
    <tbody>
        <!-- Desktop row -->
        <tr class="order-row hidden md:table-row" :class="{ 'row-selected': isSelected }" role="link" tabindex="0"
            @click="goToDetails" @keydown.enter.prevent="goToDetails" @keydown.space.prevent="goToDetails">
            <!-- Checkbox -->
            <td class="td-check" @click.stop>
                <input type="checkbox"
                    class="h-4 w-4 rounded border-gray-300 text-theme-secondary focus:ring-theme-secondary"
                    :checked="isSelected" :disabled="!order.invoice_id" @change="toggleSelection" />
            </td>

            <!-- Order number -->
            <td class="td-order">
                <p class="order-number">{{ order.order_number }}</p>
                <p v-if="order.reference_number" class="order-ref">Ref {{ order.reference_number }}</p>
            </td>

            <!-- Date -->
            <td class="td-date">
                <span class="date-text">{{ formattedOrderDate }}</span>
                <span v-if="dueDateBadge" class="due-badge" :class="dueDateBadge.type">
                    <CalendarIcon class="h-3 w-3 flex-shrink-0" />
                    {{ dueDateBadge.label }}
                </span>
            </td>

            <!-- Location -->
            <td class="td-location">
                <div v-if="locationLabel" class="location-cell">
                    <LocationMarkerIcon class="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                    <span>{{ locationLabel }}</span>
                </div>
                <span v-else class="text-gray-400">-</span>
            </td>

            <!-- Status -->
            <td>
                <span class="status-badge order" :class="orderStatusClass">{{ orderStatusLabel }}</span>
            </td>

            <!-- Payment -->
            <td class="td-payment">
                <div class="payment-cell">
                    <div class="payment-main">
                        <span class="payment-dot-indicator" :class="paymentStatusClass"></span>
                        <span class="payment-label" :class="paymentStatusClass">{{ paymentStatusLabel }}</span>
                    </div>
                    <span v-if="overdueText" class="overdue-text">
                        <span class="overdue-sep">·</span>
                        <span>{{ overdueText }}</span>
                    </span>
                </div>
            </td>

            <!-- Amount -->
            <td class="td-amount">
                <span class="amount-number">${{ orderTotal }}</span>
                <span v-if="balanceAmount" class="balance-line">Balance: ${{ balanceAmount }}</span>
            </td>

            <!-- Actions -->
            <td class="td-actions" @click.stop>
                <div class="actions-cell">
                    <button v-if="showPayButton" class="btn-pay" @click.stop="goToPay">
                        <CreditCardIcon class="h-3.5 w-3.5" />
                        <span>Pay</span>
                    </button>

                    <router-link v-if="hasInvoice" class="invoice-link" target="_blank"
                        :to="'/invoice/' + order.invoice_id">
                        <DocumentIcon class="h-3.5 w-3.5" />
                        <span>Invoice</span>
                    </router-link>

                    <span v-if="!showPayButton && !hasInvoice" class="actions-placeholder"></span>
                </div>
            </td>
        </tr>

        <!-- Mobile row -->
        <tr class="mobile-row md:hidden" :class="{ 'row-selected': isSelected }" @click="goToDetails">
            <td colspan="8" class="mobile-card-td">
                <div class="mobile-card">

                    <!-- Row 1: checkbox · order number · amount -->
                    <div class="mc-row1">
                        <div class="mc-check" @click.stop>
                            <input type="checkbox"
                                class="h-4 w-4 rounded border-gray-300 text-theme-secondary focus:ring-theme-secondary"
                                :checked="isSelected" :disabled="!order.invoice_id" @change="toggleSelection" />
                        </div>
                        <span class="order-number mc-order-num">{{ order.order_number }}</span>
                        <div class="mc-amount">
                            <span class="amount-number">${{ orderTotal }}</span>
                            <span v-if="balanceAmount" class="balance-line">Bal: ${{ balanceAmount }}</span>
                        </div>
                    </div>

                    <!-- Row 2: date · status · payment · overdue -->
                    <div class="mc-row2">
                        <span class="mc-date">{{ formattedOrderDate }}</span>
                        <span class="mc-sep">·</span>
                        <span class="status-badge order" :class="orderStatusClass">{{ orderStatusLabel }}</span>
                        <span class="payment-dot-indicator" :class="paymentStatusClass"></span>
                        <span class="payment-label" :class="paymentStatusClass">{{ paymentStatusLabel }}</span>
                        <span v-if="overdueText" class="mc-overdue">· {{ overdueText }}</span>
                    </div>

                    <!-- Row 3: location · due badge · actions -->
                    <div class="mc-row3">
                        <div class="mc-row3-left">
                            <div v-if="locationLabel" class="location-cell">
                                <LocationMarkerIcon class="h-3 w-3 flex-shrink-0 text-gray-400" />
                                <span class="mc-location-text">{{ locationLabel }}</span>
                            </div>
                            <span v-if="dueDateBadge" class="due-badge" :class="dueDateBadge.type">
                                <CalendarIcon class="h-3 w-3 flex-shrink-0" />
                                {{ dueDateBadge.label }}
                            </span>
                        </div>
                        <div v-if="showPayButton || hasInvoice" class="mc-actions" @click.stop>
                            <button v-if="showPayButton" class="btn-pay" @click.stop="goToPay">
                                <CreditCardIcon class="h-3.5 w-3.5" />
                                <span>Pay</span>
                            </button>
                            <router-link v-if="hasInvoice" class="invoice-link" target="_blank"
                                :to="'/invoice/' + order.invoice_id">
                                <DocumentIcon class="h-3.5 w-3.5" />
                                <span>Invoice</span>
                            </router-link>
                        </div>
                    </div>

                </div>
            </td>
        </tr>
    </tbody>
</template>

<script>
import { DocumentIcon, LocationMarkerIcon, CalendarIcon, CreditCardIcon } from "@heroicons/vue/outline";
import Mymixin from "@/modules/mixins";

export default {
    name: "OrdersPaymentRow",
    props: {
        order: { type: Object, required: true },
        isSelected: { type: Boolean, default: false },
        companyId: { type: String, default: null },
    },
    emits: ["toggle-select"],
    mixins: [Mymixin],
    components: {
        DocumentIcon, LocationMarkerIcon, CalendarIcon, CreditCardIcon,
    },
    computed: {
        hasInvoice() {
            return !!this.order?.invoice_id;
        },
        isVoidOrder() {
            return String(this.order?.status || "").toLowerCase() === "void";
        },
        formattedOrderDate() {
            return moment(this.order?.created_at).format("MMM Do YYYY");
        },
        locationLabel() {
            return this.order?.location || "";
        },
        orderTotal() {
            return this.commaFormat(Number(this.order?.total || 0).toFixed(2));
        },
        balanceAmount() {
            if (this.paymentStatusValue !== "partial") return null;
            const balance = this.order?.balance;
            if (!balance || Number(balance) <= 0) return null;
            return this.commaFormat(Number(balance).toFixed(2));
        },
        paymentStatusValue() {
            const n = String(this.order?.payment_status || "").toLowerCase();
            if (n === "partial") return "partial";
            if (n === "unpaid") return "unpaid";
            if (n === "paid") return "paid";
            if (n === "failed" || n.includes("declin")) return "failed";
            if (n === "pending") return "pending";
            return n || "pending";
        },
        paymentStatusLabel() {
            const labels = { paid: "Paid", pending: "Pending", unpaid: "Unpaid", failed: "Failed", partial: "Partial" };
            return labels[this.paymentStatusValue] || this.order?.payment_status || "Pending";
        },
        paymentStatusClass() {
            return this.paymentStatusValue;
        },
        overdueText() {
            const days = this.order?.days_overdue;
            if (days == null || this.paymentStatusValue === "paid") return null;
            if (days == 1) return "1 day overdue";
            if (days > 1) return `${days} days overdue`;
            if (days === 0) return "Due today";
            return null;
        },
        dueDateBadge() {
            const dueDate = this.order?.due_at;
            if (!dueDate || this.paymentStatusValue === "paid") return null;
            const due = moment(dueDate);
            const today = moment().startOf("day");
            const days = this.order?.days_overdue;
            if (days != null && days >= 0) {
                return { label: `Due ${due.format("MMM D, YYYY")}`, type: days > 0 ? "overdue" : "upcoming" };
            }
            if (due.isBefore(today)) {
                return { label: `Due ${due.format("MMM D, YYYY")}`, type: "overdue" };
            }
            return null;
        },
        showPayButton() {
            return (
                !this.isVoidOrder &&
                ["unpaid", "partial"].includes(this.paymentStatusValue)
            );
        },
        orderStatusValue() {
            return String(this.order?.status || this.order?.order_status || "")
                .trim()
                .toLowerCase()
                .replace(/[\s_]+/g, "-");
        },
        orderStatusLabel() {
            const raw = this.order?.status || this.order?.order_status || "Open";
            return String(raw).charAt(0).toUpperCase() + String(raw).slice(1).toLowerCase();
        },
        orderStatusClass() {
            const v = this.orderStatusValue;
            if (["cancelled", "canceled", "rejected", "failed"].includes(v)) return "cancelled";
            if (["void", "on-hold", "hold", "held"].includes(v)) return "void";
            if (["delivered", "complete", "completed"].includes(v)) return "delivered";
            if (["finalized", "closed", "settled", "archived"].includes(v)) return "finalized";
            if (["fulfilled", "partially-fulfilled"].includes(v)) return "fulfilled";
            if (["shipping", "shipped", "partially-shipped", "in-transit", "out-for-delivery",
                "processing", "picking", "packed", "ready-to-ship", "awaiting-shipment",
                "backordered", "backorder"].includes(v)) return "shipping";
            return "open";
        },
    },
    methods: {
        moment(date) { return moment(date); },
        commaFormat(val) {
            return Number(val).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        goToDetails() {
            this.$router.push("/order-details/" + this.order.id);
        },
        goToPay() {
            const id = this.companyId || this.order.invoice_id;
            if (id) {
                this.$router.push("/invoice-payments/" + id);
            }
        },
        toggleSelection(event) {
            this.$emit("toggle-select", { orderId: this.order.id, checked: event.target.checked });
        },
    },
};
</script>

<style lang="scss" scoped>
.order-row {
    @apply cursor-pointer transition-colors;

    &:hover td {
        background-color: #f8fafc;
    }

    &:hover .order-number {
        text-decoration: underline;
        text-decoration-color: rgba(43, 48, 63, 0.32);
    }

    &.row-selected td {
        background-color: #f5f7fa;
    }

    &.row-selected:hover td {
        background-color: #edf2f7;
    }
}

td {
    @apply px-4 py-3 whitespace-nowrap text-sm text-theme-body align-middle;
    font-size: 13px;
}

/* Checkbox column */
.td-check {
    @apply w-12 pl-4 pr-2;

    input[type="checkbox"]:disabled {
        @apply opacity-30 cursor-not-allowed;
    }
}

/* Order column */
.td-order {
    min-width: 132px;
}

.order-number {
    @apply text-sm font-semibold text-theme-secondary;
}

.order-ref {
    @apply mt-0.5 text-xs text-theme-body;
}

/* Date column */
.td-date {
    min-width: 160px;
}

.date-text {
    @apply block text-sm text-theme-body;
    font-size: 13px;
}

.due-badge {
    @apply mt-1 inline-flex items-center gap-1 rounded-full text-xs font-medium px-2 py-0.5;

    &.overdue {
        color: #dc2626;
        background: rgba(220, 38, 38, 0.08);
    }

    &.upcoming {
        color: #92400e;
        background: rgba(146, 64, 14, 0.08);
    }
}

/* Payment column */
.td-payment {
    min-width: 200px;
}

.payment-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
}

.payment-main {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    line-height: 1;
}

.payment-dot-indicator {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;

    &.paid {
        background: #16a34a;
    }

    &.pending {
        background: #d97706;
    }

    &.partial {
        background: #d97706;
    }

    &.unpaid {
        background: #dc2626;
    }

    &.failed {
        background: #dc2626;
    }
}

.payment-label {
    font-size: 13px;
    font-weight: 600;

    &.paid {
        color: #16a34a;
    }

    &.pending {
        color: #d97706;
    }

    &.partial {
        color: #d97706;
    }

    &.unpaid {
        color: #dc2626;
    }

    &.failed {
        color: #dc2626;
    }
}

.overdue-text {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    padding-left: 6px;
    color: #dc2626;
    white-space: nowrap;
}

.overdue-sep {
    line-height: 1;
    color: #f87171;
}

/* Location column */
.td-location {
    min-width: 140px;
}

.location-cell {
    @apply flex items-center gap-1.5 text-sm text-theme-body;
    font-size: 13px;
}

/* Amount column */
.td-amount {
    min-width: 110px;
    text-align: right;
    padding-right: 24px;
}

.amount-number {
    @apply block text-sm font-semibold text-theme-secondary;
    font-variant-numeric: tabular-nums;
}

.balance-line {
    @apply block mt-0.5 text-xs font-medium;
    color: #c4944a;
}

/* Actions column */
.td-actions {
    @apply text-right pl-2 pr-4;
    min-width: 168px;
}

.actions-cell {
    @apply flex items-center justify-end gap-2.5;
    min-height: 32px;
}

.actions-placeholder {
    width: 72px;
}

.btn-pay,
.invoice-link {
    @apply inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors;
    border-color: rgba(203, 213, 225, 0.9);
    background-color: #ffffff;
    color: #475569;

    &:hover {
        background-color: #f8fafc;
        border-color: rgba(148, 163, 184, 0.9);
        color: #2b303f;
    }
}

.btn-pay {
    border-color: rgba(17, 87, 64, 0.14);
    background-color: rgba(17, 87, 64, 0.05);
    color: #115740;

    &:hover {
        background-color: rgba(17, 87, 64, 0.1);
        border-color: rgba(17, 87, 64, 0.2);
        color: #0f5132;
    }
}

.invoice-link {
    &:hover {
        color: #2b303f;
    }
}

/* Status badges */
.status-badge {
    @apply inline-flex items-center rounded-full px-2.5 py-1 text-tiny font-medium uppercase tracking-wide leading-none;

    &.order {
        &.open {
            color: #1d6fa8;
            background: rgba(29, 111, 168, 0.1);
        }

        &.shipping {
            color: #3730a3;
            background: rgba(55, 48, 163, 0.1);
        }

        &.delivered {
            color: #166534;
            background: rgba(22, 101, 52, 0.1);
        }

        &.finalized {
            color: #065f46;
            background: rgba(6, 95, 70, 0.1);
        }

        &.fulfilled {
            color: #1e40af;
            background: rgba(30, 64, 175, 0.1);
        }

        &.cancelled {
            color: #991b1b;
            background: rgba(153, 27, 27, 0.1);
        }

        &.void {
            color: #92400e;
            background: rgba(146, 64, 14, 0.1);
        }
    }
}

.mobile-row {
    border-top: 1px solid #f3f4f6;

    &.row-selected .mobile-card {
        background-color: #f8fafc;
    }
}

.mobile-card-td {
    padding: 0;
    white-space: normal;
}

.mobile-card {
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

/* Row 1: checkbox · order# · amount */
.mc-row1 {
    display: flex;
    align-items: center;
    gap: 10px;
}

.mc-check {
    flex-shrink: 0;

    input[type="checkbox"]:disabled {
        @apply opacity-30 cursor-not-allowed;
    }
}

.mc-order-num {
    flex: 1;
    min-width: 0;
}

.mc-amount {
    flex-shrink: 0;
    text-align: right;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 1px;
}

/* Row 2: date · status badge · payment dot+label · overdue */
.mc-row2 {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 5px;
    padding-left: 26px;
}

.mc-sep {
    color: #d1d5db;
    font-size: 12px;
}

.mc-date {
    font-size: 12px;
    color: #6b7280;
    white-space: nowrap;
}

.mc-overdue {
    font-size: 12px;
    color: #dc2626;
    font-weight: 500;
    white-space: nowrap;
}

/* Row 3: location/due · actions */
.mc-row3 {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding-left: 26px;
}

.mc-row3-left {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    min-width: 0;
    flex: 1;
}

.mc-location-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 150px;
}

.mc-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
}
</style>
